
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PhotoUploadSection } from '@/components/interview/form/PhotoUploadSection';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PersonalInfoSectionProps {
  name: string;
  setName: (value: string) => void;
  title: string;
  setTitle: (value: string) => void;
  company: string;
  setCompany: (value: string) => void;
  photoUrl: string;
  setPhotoUrl: (value: string) => void;
  photographerCredit: string;
  setPhotographerCredit: (value: string) => void;
}

const PersonalInfoSection: React.FC<PersonalInfoSectionProps> = ({
  name,
  setName,
  title,
  setTitle,
  company,
  setCompany,
  photoUrl,
  setPhotoUrl,
  photographerCredit,
  setPhotographerCredit
}) => {
  const { t } = useTranslation();
  const [showUploader, setShowUploader] = useState(false);

  const handlePhotoUploaded = (url: string) => {
    setPhotoUrl(url);
    if (url) {
      setShowUploader(false); // Close uploader after successful upload
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          {t('production.form.name')} *
        </label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          required
        />
      </div>
      
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
          {t('production.form.title')}
        </label>
        <input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        />
      </div>
      
      <div>
        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
          {t('production.form.company')}
        </label>
        <input
          id="company"
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        />
      </div>
      
      <div>
        <label htmlFor="photoUrl" className="block text-sm font-medium text-gray-700 mb-1">
          {t('production.form.profileUrl')}
        </label>
        <input
          id="photoUrl"
          type="text"
          value={photoUrl}
          onChange={(e) => setPhotoUrl(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          placeholder="https://..."
        />
        <div className="mt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowUploader(!showUploader)}
            className="text-sm"
          >
            {showUploader ? (
              <>
                <ChevronUp className="mr-1 h-4 w-4" />
                Skjul bildeopplaster
              </>
            ) : (
              <>
                <ChevronDown className="mr-1 h-4 w-4" />
                Last opp profilbilde
              </>
            )}
          </Button>
        </div>
        
        {showUploader && (
          <div className="mt-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <PhotoUploadSection
              onPhotoUploaded={handlePhotoUploaded}
              initialPhotoUrl={photoUrl}
            />
          </div>
        )}
        
        {photoUrl && !showUploader && (
          <div className="mt-2 flex items-center gap-2">
            <img 
              src={photoUrl} 
              alt="Forhåndsvisning" 
              className="w-12 h-12 rounded-full object-cover border border-gray-200"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <span className="text-sm text-green-600">✓ Profilbilde satt</span>
          </div>
        )}
      </div>
      
      <div>
        <label htmlFor="photographerCredit" className="block text-sm font-medium text-gray-700 mb-1">
          Kreditering fotograf hvis aktuelt
        </label>
        <input
          id="photographerCredit"
          type="text"
          value={photographerCredit}
          onChange={(e) => setPhotographerCredit(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          placeholder="Foto: Navn / Organisasjon"
        />
      </div>
    </div>
  );
};

export default PersonalInfoSection;
